import { ref, computed } from "vue";
import { defineStore } from "pinia";
import { changesSaved, somethingWentWrong } from "@/helpers/utilities";
import axios from "axios";
import { getAxiosConfig, getAxiosConfigFormData } from "@/helpers/axiosConfigHelpers";

export const useRatingStore = defineStore("ratingStore", () => {
  // State
  const averageRating = ref(-1);
  const length = ref(-1);
  const loading = ref(false);
  const loadingSending = ref(false);
  const shouldFetchPostsOnClose = ref(false);
  const shouldLoadPosts = ref(false);
  const reviewId = ref(null);
  const responseId = ref(null);
  const disabledSending = ref(false);
  const index = ref(0);
  const disabled = ref(false);
  const updatedReview = ref(null);
  const updatedResponse = ref(null);
  const isFetchReviews = ref(false);
  const shouldFetchFirstPagePosts = ref(false);
  const isDeleted = ref(false);
  const isInactive = ref(false);
  const allTrades = ref([]);
  const allContractors = ref([]);
  const pagination = ref({});
  const allRegions = ref([]);
  const contractorDetails = ref({});
  const success = ref(false);

  // Getters
  const getShouldFetchPostsOnClose = computed(() => shouldFetchPostsOnClose.value);
  const getShouldLoadPosts = computed(() => shouldLoadPosts.value);
  const getAllRegions = computed(() => allRegions.value);
  const getAllTrades = computed(() => allTrades.value);
  const getSuccess = computed(() => success.value);
  const getLoading = computed(() => loading.value);
  const getShouldFetchFirstPagePosts = computed(() => shouldFetchFirstPagePosts.value);

  // Actions
  const getContractorInfo = async (contractorId) => {
    loading.value = true;
    try {
      const response = await axios.get(
        `/api/contractor/${contractorId}`,
        getAxiosConfig()
      );
      if (response.data) {
        contractorDetails.value = response.data.contractor;
      }
    } catch (err) {
      somethingWentWrong();
    } finally {
      loading.value = false;
    }
  };

  const deleteReview = async (reviewIdVal) => {
    loadingSending.value = true;
    disabledSending.value = true;
    try {
      const response = await axios.delete(
        `/api/reviews/${reviewIdVal}`,
        getAxiosConfig()
      );
      if (response.data) {
        changesSaved(response.data.message || "Review Successfully Deleted");
        reviewId.value = reviewIdVal;
      }
    } catch (err) {
      somethingWentWrong();
    } finally {
      loadingSending.value = false;
      disabledSending.value = false;
    }
  };

  const deleteResponse = async (responseIdVal) => {
    loadingSending.value = true;
    disabledSending.value = true;
    try {
      const response = await axios.delete(
        `/api/review-responses/${responseIdVal}`,
        getAxiosConfig()
      );
      if (response.data) {
        changesSaved(
          response.data.message || "Review response deleted successfully!"
        );
        responseId.value = responseIdVal;
      }
    } catch (err) {
      somethingWentWrong();
    } finally {
      loadingSending.value = false;
      disabledSending.value = false;
    }
  };

  const createResponse = async (payload) => {
    loading.value = true;
    disabled.value = true;
    try {
      const response = await axios.post(
        `/api/review-responses`,
        payload.responseData,
        getAxiosConfig()
      );
      if (response.data) {
        updatedResponse.value = response.data.review_response;
        if (!payload.dontShowSuccessSnack) {
          changesSaved(
            response.data.message || "Review response added successfully!"
          );
        }
      }
      return response;
    } catch (err) {
      somethingWentWrong();
    } finally {
      loading.value = false;
      disabled.value = false;
    }
  };

  const createResponseWithoutUpdateResponse = async (payload) => {
    loading.value = true;
    disabled.value = true;
    try {
      const response = await axios.post(
        `/api/review-responses`,
        payload.responseData,
        getAxiosConfig()
      );
      if (response.data) {
        if (!payload.dontShowSuccessSnack) {
          changesSaved(
            response.data.message || "Review response added successfully!"
          );
        }
      }
    } catch (err) {
      somethingWentWrong();
    } finally {
      loading.value = false;
      disabled.value = false;
    }
  };

  const updateReview = async (payload) => {
    loadingSending.value = true;
    disabledSending.value = true;
    try {
      let response = null;
      if (!payload.fromAdmin) {
        response = await axios.put(
          `/api/reviews/${payload.reviewId}`,
          payload.review,
          getAxiosConfig()
        );
      } else {
        response = await axios.put(
          `/api/admin/reviews/${payload.reviewId}`,
          payload.review,
          getAxiosConfig()
        );
      }
      if (response.data) {
        if (!payload.dontShowSuccessSnack) {
          changesSaved(
            response.data.message || "Review Successfully Upadated"
          );
        }
        updatedReview.value = response.data.review;
      }
    } catch (err) {
      somethingWentWrong();
    } finally {
      loadingSending.value = false;
      disabledSending.value = false;
    }
  };

  const updateResponse = async (payload) => {
    loadingSending.value = true;
    disabledSending.value = true;
    try {
      const response = await axios.patch(
        `/api/review-responses`,
        payload.responseData,
        getAxiosConfig()
      );
      if (response.data) {
        if (!payload.dontShowSuccessSnack) {
          changesSaved(
            response.data.message || "Response Successfully Upadated"
          );
        }
        updatedResponse.value = response.data.review_response;
      }
    } catch (err) {
      somethingWentWrong();
    } finally {
      loadingSending.value = false;
      disabledSending.value = false;
    }
  };

  const sendAppeal = async (payload) => {
    loading.value = true;
    disabled.value = true;
    try {
      const response = await axios.post(
        `/api/reviews/${payload.reviewId}/appeal`,
        { on_appeal_reason: payload.on_appeal_reason },
        getAxiosConfig()
      );
      if (response.data) {
        changesSaved(
          response.data.message || "Appeal is Successfully Submitted"
        );
        updatedReview.value = response.data.review;
      }
    } catch (err) {
      somethingWentWrong();
    } finally {
      loading.value = false;
      disabled.value = false;
    }
  };

  const sendTurnOffApeal = async (payload) => {
    loading.value = true;
    disabled.value = true;
    try {
      const response = await axios.post(
        `/api/reviews/${payload.reviewId}/off-appeal`,
        { off_appeal_reason: payload.off_appeal_reason },
        getAxiosConfig()
      );
      if (response.data) {
        changesSaved(
          response.data.message || "Turn off Appeal is Successfully Submitted"
        );
        updatedReview.value = response.data.review;
      }
    } catch (err) {
      somethingWentWrong();
    } finally {
      loading.value = false;
      disabled.value = false;
    }
  };

  const getAllContractors = async (payload) => {
    loading.value = true;
    try {
      const response = await axios.get(
        `/api/admin/${payload.region_id}/search-contractor?search=${payload.searchTerm}&per_page=${payload.perPage}&page=${payload.page}`,
        getAxiosConfig()
      );
      if (response.data) {
        if (payload.append) {
          allContractors.value = [
            ...allContractors.value,
            ...response.data.profiles,
          ];
        } else {
          allContractors.value = [...response.data.profiles];
        }
        pagination.value = response.data.pagination;
      }
    } catch (err) {
      somethingWentWrong();
    } finally {
      loading.value = false;
    }
  };
  const updateNotesAndAppeal = async (payload) => {
    success.value = false;
    try {
      const response = await axios.post(
        `/api/admin/reviews/${payload.reviewId}/appeal/update-appeal`,
        payload.postData,
        getAxiosConfig()
      );
      if (response.data) {
        success.value = true;
      }
    } catch (err) {
      somethingWentWrong();
    }
  };

  const getRegions = async () => {
    loading.value = true;
    try {
      const response = await axios.get(`/api/regions`, getAxiosConfig());
      if (response.data) {
        allRegions.value = response.data.regions;
      }
    } catch (err) {
      somethingWentWrong();
    } finally {
      loading.value = false;
    }
  };

  const getTrades = async (id) => {
    loading.value = true;
    try {
      const response = await axios.get(
        `/api/profileTrades/${id}`,
        getAxiosConfig()
      );
      if (response.data) {
        allTrades.value = response.data.trades;
      }
    } catch (err) {
      somethingWentWrong();
    } finally {
      loading.value = false;
    }
  };

  const deleteReviewAdmin = async (payload) => {
    loading.value = true;
    disabled.value = true;
    try {
      const response = await axios.request({
        method: "delete",
        url: `/api/admin/reviews/${payload.reviewId}`,
        data: { reason: payload.reason },
        ...getAxiosConfig(),
      });
      if (response.data) {
        changesSaved(response.message || "Review Successfully Deleted");
        setTimeout(() => {
          isDeleted.value = true;
        }, 2000);
      }
    } catch (err) {
      somethingWentWrong();
    } finally {
      loading.value = false;
      disabled.value = false;
    }
  };

  const deleteResponseAdmin = async (payload) => {
    loading.value = true;
    disabled.value = true;
    try {
      const response = await axios.request({
        method: "delete",
        url: `/api/admin/review-responses/${payload.responseId}`,
        data: { reason: payload.reason },
        ...getAxiosConfig(),
      });
      if (response.data) {
        changesSaved(response.message || "Response Successfully Deleted");
        setTimeout(() => {
          isDeleted.value = true;
        }, 2000);
      }
    } catch (err) {
      somethingWentWrong();
    } finally {
      loading.value = false;
      disabled.value = false;
    }
  };

  const deactivateReview = async ({ reason, reviewId }) => {
    loading.value = true;
    disabled.value = true;
    try {
      const response = await axios.put(
        `/api/admin/reviews/${reviewId}/deactivate`,
        { reason },
        getAxiosConfig()
      );
      if (response.data) {
        changesSaved(response.message || "Review deactivated successfully");
        setTimeout(() => {
          isInactive.value = true;
        }, 2000);
      }
    } catch (err) {
      somethingWentWrong();
    } finally {
      loading.value = false;
      disabled.value = false;
    }
  };

  const activateReview = async ({ reviewId, reason }) => {
    loading.value = true;
    disabled.value = true;
    try {
      const response = await axios.put(
        `/api/admin/reviews/${reviewId}/activate`,
        { reason },
        getAxiosConfig()
      );
      if (response.data) {
        changesSaved(response.message || "Review activated successfully");
        setTimeout(() => {
          isInactive.value = true;
        }, 2000);
      }
    } catch (err) {
      somethingWentWrong();
    } finally {
      loading.value = false;
      disabled.value = false;
    }
  };

  const deactivateResponse = async ({ responseId, reason }) => {
    loading.value = true;
    disabled.value = true;
    try {
      const response = await axios.put(
        `/api/admin/review-responses/${responseId}/deactivate`,
        { reason },
        getAxiosConfig()
      );
      if (response.data) {
        changesSaved(response.message || "Response deactivated successfully");
        setTimeout(() => {
          isInactive.value = true;
        }, 2000);
      }
    } catch (err) {
      somethingWentWrong();
    } finally {
      loading.value = false;
      disabled.value = false;
    }
  };

  const activateResponse = async ({ reason, responseId }) => {
    loading.value = true;
    disabled.value = true;
    try {
      const response = await axios.put(
        `/api/admin/review-responses/${responseId}/activate`,
        { reason },
        getAxiosConfig()
      );
      if (response.data) {
        changesSaved(response.message || "Response activated successfully");
        setTimeout(() => {
          isInactive.value = true;
        }, 2000);
      }
    } catch (err) {
      somethingWentWrong();
    } finally {
      loading.value = false;
      disabled.value = false;
    }
  };

  const updateReviewAdmin = async (payload) => {
    loading.value = true;
    disabled.value = true;
    try {
      const response = await axios.put(
        `/api/admin/reviews/${payload.reviewId}`,
        payload.review,
        getAxiosConfig()
      );
      if (response.data) {
        changesSaved(response.message || "Review Successfully Upadated");
        setTimeout(() => {
          isFetchReviews.value = true;
        }, 2000);
      }
    } catch (err) {
      somethingWentWrong();
    } finally {
      loading.value = false;
      disabled.value = false;
    }
  };

  const updateResponseAdmin = async (responseData) => {
    loading.value = true;
    disabled.value = true;
    try {
      const response = await axios.patch(
        `/api/admin/review-responses`,
        responseData,
        getAxiosConfig()
      );
      if (response.data) {
        changesSaved(response.message || "Response Successfully Upadated");
        setTimeout(() => {
          isFetchReviews.value = true;
        }, 2000);
      }
    } catch (err) {
      somethingWentWrong();
    } finally {
      loading.value = false;
      disabled.value = false;
    }
  };

  const sendAcceptRequest = async (payload) => {
    loading.value = true;
    disabled.value = true;
    try {
      const response = await axios.put(
        `/api/admin/reviews/${payload.reviewId}/accept`,
        {},
        getAxiosConfig()
      );
      if (response.data) {
        changesSaved(response.message || "Appeal is successfully accepted!");
        setTimeout(() => {
          isFetchReviews.value = true;
        }, 2000);
      }
    } catch (err) {
      somethingWentWrong();
    } finally {
      loading.value = false;
      disabled.value = false;
    }
  };

  const sendRejectRequest = async (payload) => {
    loading.value = true;
    disabled.value = true;
    try {
      const response = await axios.put(
        `/api/admin/reviews/${payload.reviewId}/reject`,
        {},
        getAxiosConfig()
      );
      if (response.data) {
        changesSaved(response.message || "Appeal is successfully rejected!");
        setTimeout(() => {
          isFetchReviews.value = true;
        }, 2000);
      }
    } catch (err) {
      somethingWentWrong();
    } finally {
      loading.value = false;
      disabled.value = false;
    }
  };

  const fetchReviews = async ({ contractorId, per_page = 10, page = 1, sortByDate = '', sortByRating = '' }) => {
    try {
      const response = await axios.get(
        `/api/reviews/${contractorId}?per_page=${per_page}&page=${page}&sort_by_date=${sortByDate}&sort_by_rating=${sortByRating}`,
        getAxiosConfig()
      )
      return response;
    } catch (error) {
      // console.error('Failed to fetch reviews:', error)
      somethingWentWrong();
      throw error
    }
  };

  const submitReview = async (review) => {
    try {
      const response = await axios.post(
        `/api/reviews`,
        review,
        getAxiosConfig()
      )
      return response;
    } catch (error) {
      somethingWentWrong(error.response.data.message);
      throw error
    }
  }


  return {
    // State
    averageRating,
    length,
    loading,
    loadingSending,
    shouldFetchPostsOnClose,
    shouldLoadPosts,
    reviewId,
    responseId,
    disabledSending,
    index,
    disabled,
    updatedReview,
    updatedResponse,
    isFetchReviews,
    shouldFetchFirstPagePosts,
    isDeleted,
    isInactive,
    allTrades,
    allContractors,
    pagination,
    allRegions,
    contractorDetails,
    success,

    // Getters

    getShouldFetchPostsOnClose,
    getShouldLoadPosts,
    getAllRegions,
    getAllTrades,
    getSuccess,
    getLoading,
    getShouldFetchFirstPagePosts,

    // Actions

    getContractorInfo,
    deleteReview,
    deleteResponse,
    createResponse,
    createResponseWithoutUpdateResponse,
    updateReview,
    updateResponse,
    sendAppeal,
    sendTurnOffApeal,
    getAllContractors,
    deactivateReview,
    activateReview,
    deactivateResponse,
    activateResponse,
    deleteReviewAdmin,
    updateNotesAndAppeal,
    updateReviewAdmin,
    getRegions,
    getTrades,
    updateResponseAdmin,
    deleteResponseAdmin,
    sendAcceptRequest,
    sendRejectRequest,
    fetchReviews,
    submitReview
  };
},
  {
    persist: true 
  }); 